import { useState } from 'react';
import type { StationarityTestResults } from '../types/wasm';

interface StationarityTestsProps {
  testResults: StationarityTestResults;
  defaultExpanded?: boolean;
}

export function StationarityTests({ testResults, defaultExpanded = false }: StationarityTestsProps) {
  const { adf, kpss } = testResults;
  const [showAdvanced, setShowAdvanced] = useState(defaultExpanded);
  const [stationarityTab, setStationarityTab] = useState<'results' | 'explanation'>('results');

  // Determine overall stationarity conclusion
  const getStationarityConclusion = () => {
    if (adf.is_stationary && kpss.is_stationary) {
      return {
        status: 'stationary',
        message: 'Series is STATIONARY',
        detail: 'Both tests agree - the series does not have a unit root and is stationary',
        color: 'text-green-600 bg-green-50 border-green-200',
        plainEnglish: 'Your data is ready for forecasting without preprocessing.',
      };
    } else if (!adf.is_stationary && !kpss.is_stationary) {
      return {
        status: 'non-stationary',
        message: 'Series is NON-STATIONARY',
        detail: 'Both tests agree - the series has a unit root and requires differencing',
        color: 'text-red-600 bg-red-50 border-red-200',
        plainEnglish: 'Differencing will be applied automatically to make the data stationary.',
      };
    } else {
      return {
        status: 'mixed',
        message: 'Tests give MIXED SIGNALS',
        detail: 'ADF and KPSS disagree - may indicate trend stationarity or structural breaks',
        color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
        plainEnglish: 'Advanced preprocessing will be applied to handle complex patterns.',
      };
    }
  };

  const conclusion = getStationarityConclusion();

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Stationarity Analysis</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              alert("Want automated AI explanations for Stationarity? Request this feature in the feedback form at the bottom of the page!");
              document.getElementById('feedback-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer mr-2"
          >
            <span>💡</span>
            <span>Request AI features</span>
          </button>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1"
          >
            <span>{showAdvanced ? 'Hide' : 'View'} Technical Details</span>
          <svg
            className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        </div>
      </div>

      {/* Simple Summary (Always Visible) */}
      <div className={`mb-6 p-4 rounded-lg border-2 ${conclusion.color}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium mb-1">Status</div>
            <div className="text-xl font-bold">{conclusion.message.replace('Series is ', '')}</div>
            <div className="text-sm mt-2">{conclusion.plainEnglish}</div>
          </div>
          <div className="text-5xl">
            {conclusion.status === 'stationary' && '✓'}
            {conclusion.status === 'non-stationary' && '✗'}
            {conclusion.status === 'mixed' && '?'}
          </div>
        </div>
      </div>

      {/* Advanced Diagnostics (Collapsible) */}
      {showAdvanced && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="p-3 bg-blue-50 rounded border border-blue-200 flex-1">
              <p className="text-xs text-blue-900">
                <strong>Technical Details:</strong> {conclusion.detail}
              </p>
            </div>
            <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200 self-end">
              <button
                type="button"
                onClick={() => setStationarityTab('results')}
                className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                  stationarityTab === 'results'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Test Results
              </button>
              <button
                type="button"
                onClick={() => setStationarityTab('explanation')}
                className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                  stationarityTab === 'explanation'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                What is Stationarity?
              </button>
            </div>
          </div>

          {stationarityTab === 'results' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ADF Test */}
        <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">
            Augmented Dickey-Fuller Test
          </h3>

          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                Test Statistic
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {adf.statistic.toFixed(4)}
              </div>
              <div className="text-xs text-blue-700 mt-1">
                More negative = stronger evidence for stationarity
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                P-Value
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {adf.p_value.toFixed(4)}
              </div>
              <div className="text-xs text-blue-700 mt-1">
                {adf.p_value < 0.05 ? '< 0.05 (Reject H₀)' : '≥ 0.05 (Fail to reject H₀)'}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">
                Result
              </div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                adf.is_stationary
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {adf.is_stationary ? 'STATIONARY' : 'NON-STATIONARY'}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="text-xs text-blue-800">
              <strong>H₀:</strong> Series has a unit root (non-stationary)<br />
              <strong>H₁:</strong> Series is stationary
            </div>
          </div>
        </div>

        {/* KPSS Test */}
        <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-4">
            KPSS Test
          </h3>

          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-purple-700 uppercase tracking-wide">
                Test Statistic
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {kpss.statistic.toFixed(4)}
              </div>
              <div className="text-xs text-purple-700 mt-1">
                Lower values indicate stationarity
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-purple-700 uppercase tracking-wide">
                Critical Value (5%)
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {kpss.critical_value_5pct.toFixed(3)}
              </div>
              <div className="text-xs text-purple-700 mt-1">
                {kpss.statistic <= kpss.critical_value_5pct
                  ? '< Critical (Fail to reject H₀)'
                  : '> Critical (Reject H₀)'}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-purple-700 uppercase tracking-wide mb-1">
                Result
              </div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                kpss.is_stationary
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {kpss.is_stationary ? 'STATIONARY' : 'NON-STATIONARY'}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-purple-200">
            <div className="text-xs text-purple-800">
              <strong>H₀:</strong> Series is stationary (opposite of ADF)<br />
              <strong>H₁:</strong> Series has a unit root
            </div>
          </div>
        </div>
      </div>
          ) : (
            <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
              <p className="font-bold text-gray-800">Understanding Time Series Stationarity</p>
              <p>
                A series is <strong>Stationary</strong> if its mean, variance, and autocorrelation structure do not change over time. Non-stationary data has trends or cycles that shift, which makes fitting statistical equations (like ARIMA) unstable.
              </p>
              <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-500">
                <li><strong>ADF (Augmented Dickey-Fuller) Test:</strong> Tests for a unit root (trend). A p-value &lt; 0.05 indicates the data is stationary.</li>
                <li><strong>KPSS Test:</strong> Tests the null hypothesis of stationarity. If the test statistic is below critical values, it confirms stationarity.</li>
                <li><strong>Handling Non-Stationarity:</strong> If either test fails, the model configuration step will automatically apply <em>differencing (d)</em> to make the data stationary.</li>
              </ul>
            </div>
          )}

          {/* Interpretation Guide */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-900 mb-3">Interpretation Guide</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-xs font-semibold text-blue-800 mb-2">Understanding the Tests</h5>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li><strong>ADF Test:</strong> Tests for unit root (null = non-stationary)</li>
                  <li><strong>KPSS Test:</strong> Tests for stationarity (null = stationary)</li>
                  <li><strong>Complementary:</strong> Use both tests together for robust conclusions</li>
                </ul>
              </div>
              <div>
                <h5 className="text-xs font-semibold text-blue-800 mb-2">Recommendations</h5>
                <ul className="text-xs text-blue-700 space-y-1">
                  {conclusion.status === 'non-stationary' && (
                    <>
                      <li>Apply differencing (d=1 or d=2 in ARIMA)</li>
                      <li>Consider log transformation if variance increases</li>
                      <li>Check for structural breaks or seasonal patterns</li>
                    </>
                  )}
                  {conclusion.status === 'stationary' && (
                    <>
                      <li>Data is ready for forecasting models</li>
                      <li>No differencing required (d=0 in ARIMA)</li>
                      <li>Focus on selecting appropriate AR and MA orders</li>
                    </>
                  )}
                  {conclusion.status === 'mixed' && (
                    <>
                      <li>Investigate for trend stationarity vs difference stationarity</li>
                      <li>Check for structural breaks or regime changes</li>
                      <li>Consider detrending or seasonal adjustment</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
